import request from '../utils/request';
import logger from '../utils/logger';
import { ProxyCommands } from '../constants/ProxyCommands';
import { PrinterSideOption } from '../constants/PrinterSideOption';
import { PERMISSION } from '../modules/newAuth/model/User';
import { observable } from 'mobx';
import { PacVersionData } from '../constants/Pac';
import { showConfirmModal } from '../modules/modals/confirmService';

class PacStore {
  @observable
    printer = null;
  @observable
    printers = [];
  @observable
    version = null;

  constructor(rootStore) {
    this.rootStore = rootStore;
  }

  get baseUrl() {
    const { configuration } = this.rootStore.settingsStore;
    const { proxyAddress, proxyPort } = configuration;
    const protocol = proxyAddress ? 'https://' : 'http://';
    const ip = proxyAddress || 'localhost';
    const port = proxyPort || 9000;

    return `${protocol}${ip}:${port}`;
  }

  userHasAccess() {
    const { newAuthStore } = this.rootStore;
    const { user } = newAuthStore;

    return user.hasAccess(PERMISSION.BATCH_PROXY);
  }

  /**
   * Print
   * options from https://www.npmjs.com/package/pdf-to-printer?activeTab=readme#api
   * @param ticket
   * @param numberOfCopies
   * @param printer
   * @returns {Promise<T>}
   */
  print(ticket, numberOfCopies = 1, printer) {
    const { configuration } = this.rootStore.settingsStore;

    return request(`${this.baseUrl}/${ProxyCommands.PRINT}`,
      {
        data: ticket,
        options: {
          copies: numberOfCopies,
          printer: printer || configuration.printer || this.printer,
          side: configuration.duplexPrinting ? PrinterSideOption.duplex : PrinterSideOption.simplex,
        },
      },
      'POST',
      { 'Content-Type': 'application/json'}
    ).then(resp => {
      if (resp.printer) {
        this.printer = resp.printer;
      }
      return resp;
    });
  }

  getPrinters() {
    logger.log('get printers', this.version);

    if (!this.version) {
      return;
    }

    const v = this.version.split('.');

    if (v[0] >= 1 && v[1] < 9) {
      logger.log('getPrinters command is available only from proxy version 1.9.0');
      return;
    }

    request(`${this.baseUrl}/${ProxyCommands.GET_PRINTERS}`,
      null,
      'GET',
      { 'Content-Type': 'application/json'}
    ).then(resp => {
      if (resp.printers) {
        this.printers = resp.printers;
        logger.table(resp.printers);
      } else {
        logger.warn('No printers', resp);
      }
    }).catch((e) => {
      logger.warn('No printers', e);
    });
  }

  connect(onOpen = () => {}, onClose = () => {}) {
    if (!window || !window.WebSocket) {
      return;
    }

    if (!this.userHasAccess()) {
      logger.warn('No permission to access the CB Proxy');
      return;
    }

    clearTimeout(this.connectionTimer);
    const { settingsStore } = this.rootStore;
    const { configuration } = settingsStore;
    const { proxyAddress, proxyWSPort } = configuration;
    const port = proxyWSPort || 9001;
    const ws = proxyAddress ? `wss://${proxyAddress}:${port}` : `ws://localhost:${port}`;
    this.ws = new WebSocket(ws);

    this.connectionTimer = setTimeout(() => {
      if (!this.isWSOpen()) {
        this.connect();
      }
    }, 5000);

    this.ws.onopen = () => {
      logger.log(`Proxy websocket connected ${ws}`);
      onOpen();
      this.onWSChange(this.rootStore.batchStore.onProxyData);
      this.getPrinters();
    };

    this.ws.onclose = (e) => {
      logger.debug('ws close', e);
      onClose();
      this.rootStore.batchStore.onProxyDisconnect(e);
      this.connectionTimer = setTimeout(() => {
        this.connect();
      }, 1000);
    };

    this.ws.onerror = (err) => {
      logger.debug('Proxy websocket encountered error: ', err, 'Closing socket');
      this.ws.close();
    };
  }

  isWSOpen() {
    logger.debug('is ws open?', this.ws?.readyState === 1, this.ws?.readyState);
    return this.ws && this.ws.readyState === 1; // 1 = open
  }

  runWS(command) {
    if (!this.userHasAccess()) {
      return;
    }

    const { batchStore } = this.rootStore;

    if (command !== ProxyCommands.GET_STATUS && command !== ProxyCommands.GET_VERSION) {
      if (!batchStore.pacVersion) {
        batchStore.showError('Plant disconnected!');
        return;
      } else if (!batchStore.isPacUidValid(batchStore.pacVersion?.uid)) {
        batchStore.showError('PAC version incorrect!');
        return;
      }
    }

    if (this.isWSOpen()) {
      if (command !== ProxyCommands.GET_STATUS) {
        batchStore.saveLog(command);
      }
      this.ws.send(command);
      logger.debug(command);
      return;
    }

    batchStore.showError('Plant disconnected!');
  }

  onWSChange(onStatusChange = () => {}) {
    const { batchStore, remoteDisplayStore, scoreboardStore } = this.rootStore;
    this.ws.addEventListener('message', event => {
      let proxyData = {};
      try {
        proxyData = JSON.parse(event.data);
      } catch(e) {
        logger.warn('Can\'t parse ws data', event.data);
        return;
      }

      logger.debug('::', proxyData.type, proxyData.data);

      switch(proxyData.type) {
        case ProxyCommands.CHECK_FAST_START_STATUS:
          if (proxyData.status) {
            logger.log('::', proxyData.type, proxyData.status);
            if (proxyData.status?.value) {
              this.showFastStartupInfoPopup();
            }
          }
          break;
        case ProxyCommands.CHECK_IS_ONLINE:
          if (proxyData.isOnline) {
            batchStore.proxyIsOnline = proxyData.isOnline;
            logger.log('::', proxyData.type, proxyData.isOnline);
          }
          break;
        case ProxyCommands.GET_PROXY_VERSION:
          if (proxyData.version) {
            this.version = proxyData.version;
            batchStore.proxyVersion = proxyData.version;
            batchStore.checkProxyNeedsUpdate();
            logger.log('::', proxyData.type, proxyData.version);
            this.getPrinters();
          }
          break;
        case ProxyCommands.RD_GET_PORTS:
          if (proxyData.ports) {
            remoteDisplayStore.savePorts(proxyData.ports);
            scoreboardStore.savePorts(proxyData.ports);
            logger.log('::', proxyData.type, proxyData.ports);
          }
          break;
        case ProxyCommands.RD_IS_PORT_OPEN:
        case ProxyCommands.RD_CONNECT:
          if (proxyData.path && !proxyData.isOpen) {
            remoteDisplayStore.onConnectFail(proxyData.path);
            scoreboardStore.onConnectFail(proxyData.path);
            logger.log('::', proxyData.type, proxyData.path, proxyData.isOpen);
          }
          break;
        case ProxyCommands.GET_VERSION:
        case ProxyCommands.GET_STATUS:
          const result = {};
          const texts = (proxyData.data || '').split('\r\n');

          texts.forEach(text => {
            const rgx = /^([-\w]+)\s(.+)$/gi;
            const match = rgx.exec(text);
            if (match) {
              result[match[1]] = match[2];
            }
          });

          if (proxyData.type === ProxyCommands.GET_VERSION) {
            if (batchStore.isPacUidValid(result.uid)) {
              batchStore.pacVersion = new PacVersionData(result);
            } else {
              batchStore.showError('PAC version incorrect!');
            }
          } else {
            onStatusChange(batchStore, result);
          }

          break;
        case ProxyCommands.SET_OUTPUT:
        case ProxyCommands.SET_OUTPUTS:
          break;
        default:
          // nothing to parse
      }

    });
  }

  showFastStartupInfoPopup() {
    showConfirmModal({
      title: 'Please disable the Fast Startup',
      message: `Go to Control Panel -> Power Options -> Choose what the power buttons do,
       click Change settings that are currently unavailable,
       then uncheck "Turn on fast startup (recommended)" and save your changes.`,
      okLabel: 'Ok',
      cancelLabel: null,
      enableEscape: false,
    }).then(() => {
      // do nothing
    }).catch(() => {
      // do nothing
    });
  }

}

export default PacStore;
